import { TooltipProvider } from '@e6/ds/core';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MainContent } from '@/components/layout/main-content';
import { Sidebar } from '@/components/layout/sidebar';
import { TopNav } from '@/components/layout/top-nav';
import { ThemeProvider } from '@/components/providers/theme-provider';
import { LoggedOutOverlay } from '@/components/ui/logged-out-overlay';
import { SessionExpiredOverlay } from '@/components/ui/session-expired-overlay';
import { onLoggedOut } from '@/lib/api/logoutService';
import { onSessionExpired } from '@/lib/api/tokenRefreshService';
import { redirectToLogin } from '@/lib/auth/redirectToLogin';
import { AuthProvider, useAuth } from '@/providers/AuthProvider';
import { ProductFeaturesProvider } from '@/providers/ProductFeaturesProvider';
import { QueryProvider } from '@/providers/QueryProvider';
import { SSEProvider } from '@/providers/SSEProvider';
import { SidebarProvider } from '@/providers/SidebarProvider';
import { API_CONFIG } from '@/config/api';
import { isStandaloneRoute } from '@/config/navigation';
import { useProactiveTokenRefresh } from '@/hooks/useProactiveTokenRefresh';
import { SHELL_LESS_ROUTES } from '@/routes';

interface RootLayoutProps {
  children: ReactNode;
}

interface SessionOverlaysProps {
  sessionExpired: boolean;
  loggedOut: boolean;
  logoutUrl?: string;
  /** Clears the overlay state owned by RootLayout. */
  onRestored: () => void;
}

/**
 * The session overlays, and the refetch that has to accompany a silent restore.
 *
 * Lives inside QueryProvider so it can reach the query client — RootLayout
 * itself sits above that provider.
 *
 * Clearing the overlay is not enough on its own. Every query that failed with a
 * 401 is parked in `error` state, and `retry` is false for this client, so
 * nothing re-runs them by itself. `refetchOnWindowFocus` does not cover it
 * either: React Query v5's focus manager listens only to `visibilitychange`,
 * and a popup never fires that — the opener tab stays `visible` throughout,
 * it merely loses focus. Without an explicit invalidate the user gets their
 * session back and keeps staring at the failed request.
 */
function SessionOverlays({
  sessionExpired,
  loggedOut,
  logoutUrl,
  onRestored,
}: SessionOverlaysProps) {
  const queryClient = useQueryClient();

  const handleRestored = useCallback(() => {
    void queryClient.invalidateQueries();
    onRestored();
  }, [queryClient, onRestored]);

  return (
    <>
      <SessionExpiredOverlay
        open={sessionExpired}
        logoutUrl={logoutUrl}
        onRestored={handleRestored}
      />
      <LoggedOutOverlay open={loggedOut} onRestored={handleRestored} />
    </>
  );
}

export function RootLayout({ children }: RootLayoutProps) {
  const [sessionExpired, setSessionExpired] = useState(false);
  const [logoutUrl, setLogoutUrl] = useState<string | undefined>();
  const [loggedOut, setLoggedOut] = useState(false);

  const handleSessionExpired = useCallback((url?: string) => {
    setLogoutUrl(url);
    setSessionExpired(true);
  }, []);

  const handleLoggedOut = useCallback(() => {
    setLoggedOut(true);
  }, []);

  const handleRestored = useCallback(() => {
    setSessionExpired(false);
    setLoggedOut(false);
    setLogoutUrl(undefined);
  }, []);

  useEffect(() => {
    const unsubscribe = onSessionExpired(handleSessionExpired);
    return unsubscribe;
  }, [handleSessionExpired]);

  useEffect(() => {
    const unsubscribe = onLoggedOut(handleLoggedOut);
    return unsubscribe;
  }, [handleLoggedOut]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <TooltipProvider delayDuration={200}>
        <AuthProvider>
          <QueryProvider>
            <SSEProvider>
              <ProductFeaturesProvider>
                <AppShell>{children}</AppShell>
                <SessionOverlays
                  sessionExpired={sessionExpired}
                  loggedOut={loggedOut}
                  logoutUrl={logoutUrl}
                  onRestored={handleRestored}
                />
              </ProductFeaturesProvider>
            </SSEProvider>
          </QueryProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

/** Waits for /me before rendering the shell to avoid sidebar layout shift */
function AppShell({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, accessDenied, user } = useAuth();
  const { pathname } = useLocation();
  // Standalone apps (e.g. Metriq, opened in a new tab) render without the sidebar.
  const standalone = isStandaloneRoute(pathname);

  // Keep the shared e6_session cookie fresh while signed in so federated
  // remotes (e.g. the Pipelines page) never hit an expired-JWT 401. The first
  // interval is sized to the session expiry from /me; each refresh response
  // then carries the new expiry, so the cadence tracks the fresh token. PLT-8655.
  useProactiveTokenRefresh(
    isAuthenticated,
    user?.sessionExpiresAt ? Date.parse(user.sessionExpiresAt) : null
  );

  if (isLoading) {
    return <AppShellSkeleton showSidebar={!standalone} />;
  }

  // accessDenied implies !isAuthenticated, so check it first
  if (accessDenied) {
    return <AccessDeniedScreen />;
  }

  if (!isAuthenticated) {
    return <UnauthenticatedScreen />;
  }

  // Authenticated, but some routes are decisions rather than destinations and
  // render without the navigation around them. See SHELL_LESS_ROUTES.
  if (SHELL_LESS_ROUTES.includes(pathname)) {
    return <>{children}</>;
  }

  return (
    <SidebarProvider>
      <div className="bg-background flex h-screen overflow-hidden">
        {!standalone && <Sidebar />}
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopNav />
          <MainContent>{children}</MainContent>
        </div>
      </div>
    </SidebarProvider>
  );
}

/**
 * Shown when no valid token is present. Auto-redirects to the control plane
 * so the user can sign in. The fallback link is for the rare case where the
 * runtime control-plane URL is missing (mis-deployed compute) or the JS
 * navigation is blocked.
 */
function UnauthenticatedScreen() {
  const controlPlaneUrl = API_CONFIG.controlPlaneUrl;

  useEffect(() => {
    redirectToLogin('unauthenticated');
  }, []);

  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <div className="text-center">
        <p className="text-muted-foreground">Redirecting to sign in…</p>
        {controlPlaneUrl ? (
          <p className="text-muted-foreground mt-2 text-sm">
            If you&apos;re not redirected,{' '}
            <a href={controlPlaneUrl} className="text-primary underline">
              open the sign-in page
            </a>
            .
          </p>
        ) : (
          <p className="text-muted-foreground mt-2 text-sm">
            Please open this workspace from the control plane to sign in.
          </p>
        )}
      </div>
    </div>
  );
}

/** Shown when /me returns 403 — user is signed in but not a member of this workspace */
function AccessDeniedScreen() {
  return (
    <div className="bg-background flex h-screen items-center justify-center">
      <div className="max-w-md text-center">
        <h2 className="text-foreground mb-2 text-xl font-semibold">Access denied</h2>
        <p className="text-muted-foreground">
          You don&apos;t have access to this workspace. Please contact your administrator to request
          access.
        </p>
      </div>
    </div>
  );
}

/** Skeleton that mirrors the real layout so there's zero visual jump */
function AppShellSkeleton({ showSidebar = true }: { showSidebar?: boolean }) {
  return (
    <div className="bg-background flex h-screen overflow-hidden">
      {/* Sidebar skeleton */}
      {showSidebar && (
        <aside className="border-sidebar-border bg-sidebar flex h-full w-48 shrink-0 flex-col border-r">
          <div className="border-border flex h-12 items-center border-b px-3">
            <span className="text-sidebar-foreground text-base font-semibold">e6data</span>
          </div>
          <div className="flex flex-col gap-2 px-2 pt-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-muted h-7 animate-pulse rounded" />
            ))}
          </div>
        </aside>
      )}
      {/* Content area skeleton */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="border-border flex h-12 items-center border-b px-4">
          <div className="bg-muted h-4 w-24 animate-pulse rounded" />
        </div>
        {/* Body skeleton */}
        <div className="flex-1 p-6">
          <div className="bg-muted mb-2 h-6 w-40 animate-pulse rounded" />
          <div className="bg-muted mb-6 h-4 w-64 animate-pulse rounded" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-muted h-16 animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
